import React, { useEffect, useState } from "react";
import { Star, Quote, Send, Loader2, MessageSquarePlus } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "../hooks/use-toast";

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    rating: 5,
    comment: "",
    service: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reviews");
      setReviews(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.comment || form.comment.length < 3) {
      toast({
        title: "Συμπληρώστε τα πεδία",
        description: "Όνομα και σχόλιο είναι υποχρεωτικά.",
      });
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      toast({
        title: "Ευχαριστούμε! ⭐",
        description:
          "Η αξιολόγησή σας θα ελεγχθεί και θα δημοσιευθεί σύντομα.",
      });
      setForm({ name: "", rating: 5, comment: "", service: "" });
      setShowForm(false);
    } catch (err) {
      toast({
        title: "Σφάλμα",
        description: "Δοκιμάστε ξανά σε λίγο.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reviews" className="py-24 relative">
      <div className="absolute inset-0 circuit-pattern opacity-20 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-end justify-between gap-6 flex-wrap reveal">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-emerald-300/80">
              Κριτικές
            </div>
            <h2 className="mt-2 text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
              Τι λένε οι <span className="text-emerald-400">πελάτες</span> μας
            </h2>
          </div>
          <button
            onClick={() => setShowForm((s) => !s)}
            className="btn-primary rounded-md px-5 py-2.5 inline-flex items-center gap-2 text-sm"
          >
            <MessageSquarePlus className="w-4 h-4" />
            {showForm ? "Άκυρο" : "Αφήστε κριτική"}
          </button>
        </div>

        {showForm && (
          <form
            onSubmit={submit}
            className="pcb-card mt-8 p-6 md:p-8 space-y-4 reveal in"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-emerald-300/80">
                  Όνομα *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Το όνομά σας"
                  className="mt-1 w-full rounded-md bg-[#04101c] border border-emerald-900/70 px-3 py-2.5 text-sm text-white placeholder:text-emerald-100/30 focus:border-emerald-500 transition-colors outline-none"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-emerald-300/80">
                  Υπηρεσία (προαιρετικό)
                </label>
                <input
                  type="text"
                  value={form.service}
                  onChange={(e) => setForm({ ...form, service: e.target.value })}
                  placeholder="π.χ. Αλλαγή οθόνης iPhone"
                  className="mt-1 w-full rounded-md bg-[#04101c] border border-emerald-900/70 px-3 py-2.5 text-sm text-white placeholder:text-emerald-100/30 focus:border-emerald-500 transition-colors outline-none"
                />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-emerald-300/80">
                Βαθμολογία
              </label>
              <div className="mt-2 flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setForm({ ...form, rating: n })}
                    className="p-1"
                    aria-label={`${n} stars`}
                  >
                    <Star
                      className={`w-7 h-7 transition-colors ${
                        n <= form.rating
                          ? "text-amber-400 fill-amber-400"
                          : "text-emerald-900/60"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-emerald-300/80">
                Σχόλιο *
              </label>
              <textarea
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
                rows={4}
                placeholder="Μοιραστείτε την εμπειρία σας..."
                className="mt-1 w-full rounded-md bg-[#04101c] border border-emerald-900/70 px-3 py-2.5 text-sm text-white placeholder:text-emerald-100/30 focus:border-emerald-500 transition-colors outline-none"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary rounded-md px-5 py-3 inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Αποστολή...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" /> Αποστολή κριτικής
                </>
              )}
            </button>
            <p className="text-[11px] text-emerald-100/40">
              Η κριτική σας θα ελεγχθεί και θα δημοσιευθεί σύντομα.
            </p>
          </form>
        )}

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {loading ? (
            <div className="col-span-full text-center py-10 text-emerald-100/60">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-emerald-400" />
              Φόρτωση κριτικών...
            </div>
          ) : reviews.length === 0 ? (
            <div className="col-span-full pcb-card p-10 text-center">
              <Quote className="w-8 h-8 text-emerald-500/50 mx-auto" />
              <div className="mt-3 text-white font-semibold">
                Δεν υπάρχουν ακόμα κριτικές
              </div>
              <div className="text-sm text-emerald-100/60 mt-1">
                Γίνετε ο πρώτος που θα μοιραστεί την εμπειρία σας!
              </div>
            </div>
          ) : (
            reviews.map((r) => (
              <div key={r.id} className="pcb-card p-6 reveal in">
                <Quote className="w-7 h-7 text-emerald-500/50" />
                <div className="mt-3 flex gap-0.5">
                  {Array.from({ length: r.rating }).map((_, k) => (
                    <Star
                      key={k}
                      className="w-4 h-4 text-amber-400 fill-amber-400"
                    />
                  ))}
                </div>
                <p className="mt-3 text-sm text-emerald-50/85 leading-relaxed whitespace-pre-wrap">
                  “{r.comment}”
                </p>
                <div className="mt-5 pt-4 border-t border-emerald-900/50">
                  <div className="text-white font-semibold text-sm">{r.name}</div>
                  {r.service && (
                    <div className="text-xs text-emerald-300/70">{r.service}</div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default Reviews;
